// @ts-ignore
import markdownit, { Token } from 'markdown-it';
// @ts-ignore
import markdownItFootnote from 'markdown-it-footnote';
// @ts-ignore
import markdownItEmoji from 'markdown-it-emoji';
// @ts-ignore
import markdownVideo from 'markdown-it-video';

export interface IMarkdownViewerPlugin {
    token: RegExp;
    transform: (all: string, separator: string, match: string) => string;
}

export interface IMarkdownViewerPluginCustom {
    transformPreProcess: (text: string) => string;
    transformPostProcess: (text: string) => string;
}

export interface IEditorPlugin {
    preProcess?: (inputValue: string) => string;
    postProcess?: (text: string) => string;
    registerEvents: (editor: EasyMDE) => void;
}

export interface IMarkdownViewer {
    use: (plugin: IMarkdownViewerPluginCustom) => IMarkdownViewer;
    buildOptions: () => IMarkdownViewer;
    toHtml: (markdown: string) => string;
    generalPreProcess: (text: string) => string;
    generalPostProcess: (text: string) => string;
    preprocess: (text: string) => string;
    postProcess: (text: string) => string;

    processMarkdownToHtml: (text: string) => string;
}

let sharedMarkdownIt: any = null;

const getSharedMarkdownIt = () => {
    if (!sharedMarkdownIt) {
        const md = new markdownit({
            html: false, // Enable HTML tags in source
            xhtmlOut: false, // Use '/' to close single tags (<br />).
            // This is only for full CommonMark compatibility.
            breaks: true, // Convert '\n' in paragraphs into <br>
            langPrefix: 'language-', // CSS language prefix for fenced blocks. Can be
            // useful for external highlighters.
            linkify: true, // Autoconvert URL-like text to links
            // Enable some language-neutral replacement + quotes beautification
            typographer: false,
            // Double + single quotes replacement pairs, when typographer enabled,
            // and smartquotes on. Could be either a String or an Array.
            //
            // For example, you can use '«»„“' for Russian, '„“‚‘' for German,
            // and ['«\xA0', '\xA0»', '‹\xA0', '\xA0›'] for French (including nbsp).
            quotes: '“”‘’',
            // Highlighter function. Should return escaped HTML,
            // or '' if the source string is not changed and should be escaped externaly.
            // If result starts with <pre... internal wrapper is skipped.
            highlight: function(/*str, lang*/) {
                return '';
            },
        });

        md.disable(['code']);

        md.use(markdownItFootnote);
        md.use(markdownItEmoji);
        md.use(markdownVideo);
        sharedMarkdownIt = md;
    }

    return sharedMarkdownIt;
};

export class MarkdownViewer {
    private plugins: IMarkdownViewerPluginCustom[];
    private options: any;

    constructor() {
        this.plugins = [];
        this.options = null;
    }

    use(plugin: IMarkdownViewerPluginCustom): MarkdownViewer {
        this.plugins.push(plugin);
        return this;
    }

    buildOptions(): MarkdownViewer {
        //var tokenizers = this.plugins.map(x => x);

        this.options = {};

        return this;
    }

    generalPreProcess = (text: string): string => {
        return text
            .replace(/\n/g, '&nbsp;&nbsp;\n')
            .replace(/\r/g, '<br/>')
            .replace(/\t/g, '    ');
    };

    generalPostProcess = (text: string): string => {
        return text
            .replace(/\n/g, '  \n')
            .replace(/\r/g, '<br/>')
            .replace(/\t/g, '    ');
    };

    preprocess = (text: string): string => {
        let processText = ''.concat(text);
        //processText = this.generalPreProcess(processText);
        this.plugins.forEach((plugin) => {
            processText = plugin.transformPreProcess(processText);
        });

        return processText;
    };

    postProcess = (text: string): string => {
        let processText = ''.concat(text);
        //processText = this.generalPostProcess(processText);
        this.plugins.forEach((plugin) => {
            processText = plugin.transformPostProcess(processText);
        });

        return processText;
    };

    processMarkdownToHtml = (text: string): string => {
        const processText = ''.concat(text);

        const md = getSharedMarkdownIt();

        return md.render(processText);
    };

    toHtml(markdownStr: string): string {
        let text = ''.concat(markdownStr);
        text = this.preprocess(text);
        text = this.processMarkdownToHtml(text);
        text = this.postProcess(text);
        return text;
    }
}

export const mentionsRegex = /@((\s?[^\s@]+)+(\({1}\S+@\S+\.\S+\){1})?(@\S+\.\S+)?)/g;

export class MentionMarkdownViewPluginCustom implements IMarkdownViewerPluginCustom {
    transformPreProcess(text: string): string {
        return text;
    }

    transformPostProcess(text: string): string {
        const results: Array<any> = [];
        let match;
        while ((match = mentionsRegex.exec(text)) != null) {
            // Find all matches in a string
            results.push({
                // Btw: match[0] is the complete match
                full: match[0],
            });
        }
        results.forEach((mention) => {
            text = text.replace(mention.full, `<span class="mention">${mention.full}</span>`);
        });

        return text;
    }
}

export class MentionFullMarkdownViewPluginCustom implements IMarkdownViewerPluginCustom {
    transformPreProcess(text: string): string {
        const results: Array<any> = [];
        const mentionsFullRegex = /[@]{1}\[([^\]]+)\]\([id:]{3}([^\)]+?)\)+/g;
        let match;
        while ((match = mentionsFullRegex.exec(text)) != null) {
            // Find all matches in a string
            results.push({
                // Btw: match[0] is the complete match
                full: match[0],
                value: match[1],
                id: match[2],
            });
        }
        results.forEach((mention) => {
            text = text.replace(mention.full, `@[${mention.value}]|(id:${mention.id})`);
        });

        return text;
    }

    transformPostProcess(text: string): string {
        const results: Array<any> = [];
        const mentionsFullRegex = /[@]{1}\[([^\]]+)\]\|\([id:]{3}([^\)]+?)\)+/g;
        let match;
        while ((match = mentionsFullRegex.exec(text)) != null) {
            // Find all matches in a string
            results.push({
                // Btw: match[0] is the complete match
                full: match[0],
                value: match[1],
                id: match[2],
            });
        }
        results.forEach((mention) => {
            text = text.replace(mention.full, `<span class="mention">@${mention.value}</span>`);
        });

        return text;
    }
}

//plugin for outputting ins html tag when html is disabled
export class InsMarkdownViewPluginCustom implements IMarkdownViewerPluginCustom {
    transformPreProcess(text: string): string {
        return text;
    }

    transformPostProcess(text: string): string {
        return text.replace(/&lt;ins&gt;/g, '<ins>').replace(/&lt;\/ins&gt;/g, '</ins>');
    }
}

//plugin for outputting del html tag when html is disabled (for diff highlighting)
export class DelMarkdownViewPluginCustom implements IMarkdownViewerPluginCustom {
    transformPreProcess(text: string): string {
        return text;
    }

    transformPostProcess(text: string): string {
        return text.replace(/&lt;del&gt;/g, '<del>').replace(/&lt;\/del&gt;/g, '</del>');
    }
}

export class LinkMarkdownViewPluginCustom implements IMarkdownViewerPluginCustom {
    transformPreProcess(text: string): string {
        return text;
    }

    transformPostProcess(text: string): string {
        const results: Array<any> = [];
        const selfLinkPostRegex = /<a\s+(?:[^>]*?\s+)?href=\"(.*?)\"[^>]*>(.*?)<\s*\/\s*a>/g;
        let match;
        while ((match = selfLinkPostRegex.exec(text)) != null) {
            // Find all matches in a string
            results.push({
                // Btw: match[0] is the complete match
                full: match[0],
                label: match[2],
                href: match[1],
            });
        }

        // eslint-disable-next-line
        // const selfUri = new URL(document.location.href);
        // eslint-disable-next-line
        results.forEach((link) => {
            // const isLinkValid = utils.isUrl(link.href);
            // const linkUri = isLinkValid ? new URL(link.href) : null;
            // /*
            //  * Self links
            //  */
            // if (isLinkValid && linkUri.hostname === selfUri.hostname) {
            //     const label =
            //         link.label === link.href ? viewmodels.storymap.BoardNavigationHelper.getNameOfItemOnBoard(link.href) : link.label;
            //     const onClick = `viewmodels.storymap.BoardNavigationHelper.openLinkFromBoard('${link.href}', event.ctrlKey); event.stopPropagation();`;
            //     text = text.replace(link.full, `<a class="self-link" onclick="${onClick}">${label}</a>`);
            // } else {
            text = text.replace(
                link.full,
                `<a href="${link.href}" onclick="event.stopPropagation();" target="_blank" rel="noopener">${link.label}</a>`
            );
            // }
        });

        return text;
    }
}

export class DiffLineMarkdownViewPluginCustom implements IMarkdownViewerPluginCustom {
    transformPreProcess(text: string): string {
        return text;
    }

    transformPostProcess(text: string): string {
        const addedSentinel = '[[SB_LINE_ADDED]]';
        const removedSentinel = '[[SB_LINE_REMOVED]]';

        /*
         * Helper to add a CSS class to an opening tag.
         * Examples:
         *   <p>           -> <p class="sb-line-added">
         *   <p class="x"> -> <p class="x sb-line-added">
         */
        const addClassToOpenTag = (openTag: string, className: string): string => {
            if (openTag.includes('class="')) {
                return openTag.replace(/class="([^"]*)"/, (_m, classes) => `class="${classes} ${className}"`);
            }
            return openTag.replace(/^<([a-z1-6]+)/i, `<$1 class="${className}"`);
        };

        const processLine = (line: string): string => {
            let updated = line;

            // Only decorate known block elements.
            const tagMatch = updated.match(/<(p|li|h[1-6]|td)([^>]*)>/);
            if (!tagMatch) {
                // Still strip any stray sentinels if present.
                return updated.replace(addedSentinel, '').replace(removedSentinel, '');
            }

            const fullTag = tagMatch[0];

            if (updated.includes(removedSentinel)) {
                const newTag = addClassToOpenTag(fullTag, 'sb-line-removed');
                updated = updated.replace(fullTag, newTag);
                updated = updated.replace(removedSentinel, '');
            }

            if (updated.includes(addedSentinel)) {
                const newTag = addClassToOpenTag(
                    (updated.match(/<(p|li|h[1-6]|td)([^>]*)>/) || [fullTag])[0],
                    'sb-line-added'
                );
                updated = updated.replace(/<(p|li|h[1-6]|td)([^>]*)>/, newTag);
                updated = updated.replace(addedSentinel, '');
            }

            return updated;
        };

        const lines = text.split('\n').map(processLine);

        // Final safety: remove any sentinels that slipped through.
        return lines.join('\n').replace(/\[\[SB_LINE_ADDED\]\]|\[\[SB_LINE_REMOVED\]\]/g, '');
    }
}

export class AttachmentLinkMarkdownViewPluginCustom implements IMarkdownViewerPluginCustom {
    transformPreProcess(text: string): string {
        const results: Array<any> = [];
        const attachmentLinkRegex = /(^|[^!])\[([^\]]+?)\]\([/]{1}([^\)]+?)\)+/g;
        let match;
        while ((match = attachmentLinkRegex.exec(text)) != null) {
            // Find all matches in a string
            results.push({
                // Btw: match[0] is the complete match
                full: match[0],
                prefix: match[1],
                label: match[2],
                href: match[3],
            });
        }

        results.forEach((link) => {
            text = text.replace(link.full, `${link.prefix}[${link.label}]|(/${link.href})`);
        });

        return text;
    }

    transformPostProcess(text: string): string {
        const results: Array<any> = [];
        const attachmentLinkRegex = /\[([^\]]+?)\]\|\([/]{1}([^\)]+?)\)+/g;
        let match;
        while ((match = attachmentLinkRegex.exec(text)) != null) {
            // Find all matches in a string
            results.push({
                // Btw: match[0] is the complete match
                full: match[0],
                label: match[1],
                href: match[2],
            });
        }

        const selfUri = new URL(document.location.href);

        results.forEach((link) => {
            text = text.replace(
                link.full,
                `<a href="${selfUri.origin}/${link.href}" onclick="event.stopPropagation();" target="_blank">${link.label}</a>`
            );
        });

        return text;
    }
}

export const useNotificationMdParser = () => {
    const mdViewer = new MarkdownViewer()
        .use(new MentionFullMarkdownViewPluginCustom())
        .use(new LinkMarkdownViewPluginCustom())
        .use(new AttachmentLinkMarkdownViewPluginCustom())
        .use(new DiffLineMarkdownViewPluginCustom())
        .use(new InsMarkdownViewPluginCustom())
        .use(new DelMarkdownViewPluginCustom())
        .buildOptions();

    return {
        mdViewer,
    };
};

export const useSimpleMdParser = () => {
    const mdViewer = new MarkdownViewer().buildOptions();

    return {
        mdViewer,
    };
};

export const useCommentMdParser = () => {
    const mdViewer = new MarkdownViewer()
        .use(new MentionFullMarkdownViewPluginCustom())
        .use(new LinkMarkdownViewPluginCustom())
        .use(new AttachmentLinkMarkdownViewPluginCustom())
        .buildOptions();

    return {
        mdViewer,
    };
};

export const useStorymapCardDetailsDescriptionParser = () => {
    const mdViewer = new MarkdownViewer()
        .use(new MentionFullMarkdownViewPluginCustom())
        .use(new LinkMarkdownViewPluginCustom())
        .use(new AttachmentLinkMarkdownViewPluginCustom())
        .buildOptions();

    return {
        mdViewer,
    };
};
